import React, { useEffect, useState } from 'react';
import { useAuthenticateRegistryReservationMutation } from '@graphql/mutations/order.graphql.generated';
import { useGetEventByEventIdForEventRedirectQuery } from '@graphql/queries/event.graphql.generated';
import type { StaticRouterProps, RouteComponentProps } from '@react-router';
import type { QueryStringMap } from '@shared/core/queryString';
import { deriveQueryStringHelpersFromLocation } from '@shared/core/queryString';
import { JoyLogoLoader } from '../JoyLogoLoader';
import { withWindow } from '@shared/utils/withWindow';
import { Box, Flex } from '@withjoy/joykit';
import { ReactComponent as SurprisedCakeSvg } from '@assets/icons/spot-art-cake-surprised.svg';
import { useTranslation } from '@shared/core';
import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import type { ApolloError } from '@apollo/client';
import { config } from '@static/js/env.config';

/**
 * ASYNC REDIRECT ISSUE IN SSR
 * --------------------------
 * This component has a fundamental issue with how it handles asynchronous operations during SSR.
 *
 * The end result is that authenticated redirects do not complete on server / in SSR.
 * Instead, the page renders -- and the client-side code does the heavy lifting.
 * So authenticated links "work", but they're half broken and a very slow user experience.
 *
 * Problem:
 * 1. In SSR mode, the component attempts to perform redirects based on async GraphQL queries
 * 2. For registry paths, it also attempts to call an async mutation (authenticateRegistryReservation)
 * 3. The SSR redirect happens immediately at line ~116 without waiting for async operations to complete
 * 4. Different code paths are taken between SSR and client-side rendering
 *
 * This creates race conditions where:
 * - SSR might redirect before data is available
 * - Client might re-render and redirect differently during hydration
 * - The async registry authentication may never complete in SSR context
 *
 * To properly fix this:
 * 1. Move async data fetching to server route handlers before React rendering
 * 2. Complete all GraphQL operations before deciding on redirects
 * 3. Use proper SSR redirect mechanisms (e.g., res.redirect() in Express)
 * 4. Simplify the component to handle only client-side cases and error states
 */

type EventRedirectProps = Readonly<
  {
    eventId: string;
    staticContext: StaticRouterProps['context'];
    onNoMatch?: (args: { eventId: string }) => void;
  } & Pick<RouteComponentProps, 'location'>
>;

export const REGISTRY_PATH = '/registry';

const telemObject = createTelemetryObject({
  actions: {
    eventNotFoundError: (eventId: string, destination: string) => ({
      category: 'event.redirect',
      actionType: 'error',
      action: 'EventNotFound',
      label: eventId,
      extraInfo: {
        destination,
      },
    }),
    findEventGqlError: (eventId: string, destination: string, error: ApolloError) => ({
      category: 'event.redirect',
      actionType: 'error',
      action: 'EventNotFound',
      label: eventId,
      extraInfo: {
        destination,
        error,
      },
    }),
    authenticateReservationError: (eventId: string, authenticateUrl: string) => ({
      category: 'event.redirect',
      actionType: 'error',
      action: 'AuthenticateRegistryResultError',
      label: eventId,
      extraInfo: {
        authenticateUrl,
      },
    }),
  },
});

const { TelemetryProvider, useTelemetry } = createTelemetry(telemObject);

const ErrorScreen: React.FC<{ hed: string; dek: string }> = ({ hed, dek }) => {
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      marginTop="80px"
    >
      <Flex
        flexDirection="column"
        width={320}
        maxWidth="100%"
        alignItems="center"
        textAlign="center"
      >
        <SurprisedCakeSvg />
        <Box
          fontFamily="'Inter UI'"
          fontSize="32px"
          fontWeight="bold"
          lineHeight={1.3125}
          marginTop={8}
        >
          {hed}
        </Box>
        <Box
          fontFamily="'Inter UI'"
          fontSize="17px"
          lineHeight={1.588}
          marginTop={6}
          width="90%"
        >
          {dek}
        </Box>
      </Flex>
    </Flex>
  );
};

const Page = (props: EventRedirectProps) => {
  const { eventId, staticContext, location, onNoMatch } = props;
  const [authenticateRegistryReservation, authenticateRegistryResult] = useAuthenticateRegistryReservationMutation();
  const { data, error, loading } = useGetEventByEventIdForEventRedirectQuery({ variables: { id: eventId }, batchMode: 'fast' });
  const telemetry = useTelemetry();
  const { t2 } = useTranslation('eventSite');
  const { getParamNames, getValue, getValueString, buildRedirectPath } = deriveQueryStringHelpersFromLocation(location);
  const destination = getValueString('destination') || '';
  const authenticateUrl = config.clientUri + `${location.pathname}${location.search}` || '';
  const shouldCallAuthenticateRegistry = destination.startsWith(REGISTRY_PATH) && location.search.includes('token=');
  const [isReadyToCheck, setReadyToCheck] = useState<boolean>(false);
  const isAllLoaded = !loading;
  const noEventFound = !data?.eventById?.website;

  // forward all parameters except &destination
  const nextRoute = `/${data?.eventById?.website}${destination}`;
  const nextParams = getParamNames().reduce((acc: QueryStringMap, key: string) => {
    if (key === 'destination') {
      return acc;
    }
    acc[key] = getValue(key) ?? null;
    return acc;
  }, {});
  const nextPath = buildRedirectPath(nextRoute, nextParams);

  const performRedirect = (path: string) => {
    if (staticContext) {
      if (noEventFound) {
        staticContext.statusCode = 404;
      } else {
        staticContext.url = path;
        staticContext.statusCode = 302;
      }
    } else {
      // Is client-side rendered.
      withWindow(global => {
        const href = global.location.origin + (noEventFound ? '/404' : path);
        global.location.href = href;
      });
    }
  };
  if (!shouldCallAuthenticateRegistry && staticContext && isAllLoaded) {
    performRedirect(nextPath);
  }

  useEffect(() => {
    if (shouldCallAuthenticateRegistry) {
      authenticateRegistryReservation({
        variables: {
          payload: {
            url: authenticateUrl,
          },
        },
      });
    } else {
      setReadyToCheck(isAllLoaded);
    }
  }, [authenticateRegistryReservation, authenticateUrl, shouldCallAuthenticateRegistry, isAllLoaded]);

  useEffect(() => {
    if (authenticateRegistryResult.error) {
      telemetry.authenticateReservationError(eventId, authenticateUrl);
      setReadyToCheck(isAllLoaded);
      // If there is an error in it, it returns nothing here so that the current flow is not broken.
    }
    if (authenticateRegistryResult.called && !authenticateRegistryResult.loading && !authenticateRegistryResult.error) {
      setReadyToCheck(isAllLoaded);
    }
  }, [authenticateRegistryResult, authenticateUrl, eventId, telemetry, isAllLoaded]);

  let content = <JoyLogoLoader loaderKey="event-redirect" />;
  if (isReadyToCheck) {
    if (data) {
      // Query was successful
      const eventHandle = data.eventById?.website;
      if (eventHandle) {
        performRedirect(nextPath);
      } else {
        // Event could not be found
        onNoMatch?.({ eventId });
        telemetry.eventNotFoundError(eventId, destination);
        const translations = t2('eventRedirect', 'eventNotFoundError');
        content = <ErrorScreen {...translations} />;
      }
    } else if (error) {
      // The default apollo error policy is if the GQL response includes errors, they are returned on
      // error.graphqlErrors and the response `data` is set to undefined even if the server returns `data`
      // in its response.
      telemetry.findEventGqlError(eventId, destination, error);
      const translations = t2('eventRedirect', 'graphqlError');
      content = <ErrorScreen {...translations} />;
    }
  }

  return content;
};

export const EventRedirect = (props: EventRedirectProps) => {
  return (
    <TelemetryProvider>
      <Page {...props} />
    </TelemetryProvider>
  );
};
